import React, { useEffect, useState, useCallback } from "react";
import { facilityAdmin } from "../../api";
import {
  PageHeader, Btn, EmptyState, LoadingSpinner,
  ConfirmModal, FormField, inputStyle, selectStyle,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

// Facility-admin configuration for how patients get categorized at
// issuance — e.g. "Insured" vs "Not Insured" (or whatever criteria a
// facility uses), each with its own ticket-number prefix. The kiosk asks
// this as one of the patient-details questions (enable it under the
// "insurance" kiosk field) and shows exactly the rules configured here as
// buttons. Numbering itself stays one shared sequential count per day —
// only the prefix on the ticket changes, so "Insured" and "Not Insured"
// tickets interleave in the same queue order they were issued in
// (e.g. INS-012, NON-013, INS-014...), not two separate counts.
export default function InsuranceRulesPage() {
  const [rules,   setRules]   = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving,  setSaving]  = useState(false);

  // The kiosk "insurance" field is what actually asks patients this
  // question and feeds their answer into the rules below — without it
  // enabled, insurance_type is always blank on every ticket, so none of
  // these rules ever match and every patient silently falls back to the
  // default station + its own prefix. This used to be a dead-end: the
  // hint here pointed at "Settings → Kiosk" for a toggle that doesn't
  // exist anywhere in the app (facilityadmin/SettingsPage.js has no
  // insurance-related control at all). Managing it directly here — where
  // the rules it depends on already live — closes that gap.
  const [kioskFields,     setKioskFields]     = useState([]);
  const [fieldSaving,     setFieldSaving]     = useState(false);
  const insuranceField = kioskFields.find(f => f.field_name === "insurance") || null;
  const insuranceFieldOn = !!(insuranceField && insuranceField.is_enabled && insuranceField.affects_routing);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rr, sr, kr] = await Promise.all([
        facilityAdmin.listInsuranceRules(),
        facilityAdmin.listStations(),
        facilityAdmin.listKioskFields(),
      ]);
      setRules(rr.data?.results || rr.data);
      setStations(sr.data?.results || sr.data);
      setKioskFields(kr.data?.results || kr.data || []);
    } catch { toast.error("Failed to load rules."); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleInsuranceField = async (next) => {
    setFieldSaving(true);
    try {
      if (insuranceField) {
        await facilityAdmin.updateKioskField(insuranceField.id, {
          is_enabled: next, affects_routing: next,
        });
      } else if (next) {
        await facilityAdmin.createKioskField({
          field_name: "insurance",
          display_label_en: "Insurance Status",
          display_label_tw: "",
          field_type: "select",
          is_required: true,
          is_enabled: true,
          affects_routing: true,
          sort_order: kioskFields.length,
        });
      }
      const kr = await facilityAdmin.listKioskFields();
      setKioskFields(kr.data?.results || kr.data || []);
      toast.success(next ? "Kiosk will now ask for insurance status." : "Insurance question removed from kiosk.");
    } catch {
      toast.error("Failed to update kiosk field.");
    } finally {
      setFieldSaving(false);
    }
  };

  const save = async (form) => {
    setSaving(true);
    try {
      const payload = { ...form, insurance_value: form.insurance_value.trim().toLowerCase() };
      if (form.id) await facilityAdmin.updateInsuranceRule(form.id, payload);
      else         await facilityAdmin.createInsuranceRule(payload);
      toast.success(`Rule ${form.id ? "updated" : "created"}.`);
      setModal(null); load();
    } catch (e) {
      toast.error(e.response?.data?.insurance_value?.[0] || e.response?.data?.detail || "Failed to save rule.");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    try {
      await facilityAdmin.deleteInsuranceRule(deleteConfirm.id);
      toast.success("Deleted.");
      setDeleteConfirm(null); load();
    } catch { toast.error("Delete failed."); }
    finally  { setSaving(false); }
  };

  const stationName = (id) => stations.find(s => s.id === id)?.name;

  return (
    <div style={styles.root}>
      <PageHeader
        title="Insurance / Patient Categories"
        subtitle='Define how patients get categorized when they check in — e.g. "Insured" vs "Not Insured" — and the ticket-number prefix each category gets. Numbering stays one shared sequence per day; only the prefix changes.'
        action={<Btn onClick={() => setModal("create")}>+ New Category</Btn>}
      />

      <div style={styles.hint}>
        <div style={{ flex: 1 }}>
          <strong style={{ color: "var(--text-primary)" }}>Ask patients this on the kiosk</strong>
          <div style={{ marginTop: "2px" }}>
            These rules only take effect once the kiosk actually asks patients to pick a category —
            without this on, every ticket falls back to the normal station and its default prefix.
          </div>
        </div>
        <MiniToggle checked={insuranceFieldOn} disabled={fieldSaving} onChange={toggleInsuranceField} />
      </div>

      {loading ? <LoadingSpinner /> : rules.length === 0 ? (
        <EmptyState
          icon="🏷️" title="No categories yet"
          subtitle='Add one, e.g. "Insured" with prefix INS, and another "Not Insured" with prefix NON — the kiosk will show these as buttons once configured.'
          action={<Btn onClick={() => setModal("create")}>+ Create First Category</Btn>}
        />
      ) : (
        <div style={styles.list}>
          {rules.map(r => (
            <div key={r.id} style={styles.row}>
              <div style={styles.rowMain}>
                <div style={styles.rowTitle}>
                  {r.display_label_en}
                  <span style={styles.prefixChip}>{r.queue_prefix}</span>
                </div>
                <div style={styles.rowMeta}>
                  value: <code>{r.insurance_value}</code>
                  {r.target_station && <> · routes to <strong>{stationName(r.target_station)}</strong></>}
                  {r.first_stop_station && <> · first stop <strong>{stationName(r.first_stop_station)}</strong></>}
                </div>
              </div>
              <div style={styles.rowActions}>
                <Btn size="sm" variant="ghost" onClick={() => setModal(r)}>Edit</Btn>
                <Btn size="sm" variant="danger" onClick={() => setDeleteConfirm(r)}>Delete</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      <RuleFormModal
        open={!!modal}
        onClose={() => setModal(null)}
        initial={modal !== "create" ? modal : null}
        stations={stations}
        onSave={save}
        saving={saving}
      />
      <ConfirmModal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        loading={saving}
        title="Delete category?"
        message={`"${deleteConfirm?.display_label_en}" will be permanently deleted. Existing tickets already issued keep their prefix.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}

function RuleFormModal({ open, onClose, initial, stations, onSave, saving }) {
  const blank = { insurance_value: "", display_label_en: "", display_label_tw: "", queue_prefix: "", target_station: "", first_stop_station: "" };
  const [form, setForm] = useState(blank);
  useEffect(() => {
    if (open) setForm(initial ? {
      insurance_value: initial.insurance_value || "",
      display_label_en: initial.display_label_en || "",
      display_label_tw: initial.display_label_tw || "",
      queue_prefix: initial.queue_prefix || "",
      target_station: initial.target_station || "",
      first_stop_station: initial.first_stop_station || "",
    } : blank);
  }, [open, initial]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const valid = form.insurance_value.trim() && form.display_label_en.trim() && form.queue_prefix.trim();

  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit Category" : "New Category"} width="480px">
      <div style={F.form}>
        <FormField label="Category Name (English)" required hint='Shown as the button label on the kiosk, e.g. "Insured"'>
          <input style={inputStyle} value={form.display_label_en} onChange={e => set("display_label_en", e.target.value)} placeholder="e.g. Insured" />
        </FormField>
        <FormField label="Category Name (Twi)" hint="Optional — falls back to English">
          <input style={inputStyle} value={form.display_label_tw} onChange={e => set("display_label_tw", e.target.value)} placeholder="Twi label" />
        </FormField>
        <div style={F.row2}>
          <FormField label="Internal Value" required hint="Lowercase, no spaces — used internally, not shown to patients">
            <input style={inputStyle} value={form.insurance_value} onChange={e => set("insurance_value", e.target.value)} placeholder="e.g. insured" />
          </FormField>
          <FormField label="Ticket Prefix" required hint='e.g. "INS" — replaces the usual station prefix'>
            <input style={inputStyle} value={form.queue_prefix} onChange={e => set("queue_prefix", e.target.value.toUpperCase())} placeholder="e.g. INS" maxLength={5} />
          </FormField>
        </div>
        <FormField label="Route to Station" hint="Optional — leave blank to keep normal routing (only the prefix changes)">
          <select style={selectStyle} value={form.target_station} onChange={e => set("target_station", e.target.value)}>
            <option value="">— Normal routing —</option>
            {stations.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </FormField>
        <FormField label="First stop after the front desk" hint="Optional — e.g. Cashier for Cash / NON. The transfer screen preselects it for fresh tickets; staff can still choose another outcome after a confirmation.">
          <select style={selectStyle} value={form.first_stop_station} onChange={e => set("first_stop_station", e.target.value)}>
            <option value="">— None —</option>
            {stations.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </FormField>
        <div style={F.footer}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={() => onSave(initial ? { ...form, id: initial.id } : form)} disabled={saving || !valid}>
            {saving ? "Saving…" : initial ? "Save Changes" : "Create Category"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function MiniToggle({ checked, disabled, onChange }) {
  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        flexShrink: 0, width: "40px", height: "22px", borderRadius: "11px", border: "none",
        cursor: disabled ? "not-allowed" : "pointer", position: "relative", padding: 0,
        background: checked ? "#2563EB" : "var(--input-border)", opacity: disabled ? 0.6 : 1,
        transition: "background 0.15s",
      }}
    >
      <div style={{
        position: "absolute", top: "2px", width: "18px", height: "18px", borderRadius: "50%",
        background: "#fff", transition: "transform 0.15s",
        transform: checked ? "translateX(20px)" : "translateX(2px)",
      }} />
    </button>
  );
}

const styles = {
  root: { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "900px" },
  hint: {
    display: "flex", alignItems: "center", gap: "16px",
    fontSize: "12px", color: "var(--text-muted)", marginTop: "-8px", marginBottom: "20px",
    background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "10px", padding: "12px 14px",
  },
  list: { display: "flex", flexDirection: "column", gap: "10px" },
  row: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: "16px", padding: "14px 18px", borderRadius: "12px",
    background: "var(--surface-bg)", border: "1px solid var(--surface-border)",
  },
  rowMain: { flex: 1, minWidth: 0 },
  rowTitle: { fontSize: "14px", fontWeight: "600", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "8px" },
  prefixChip: {
    fontSize: "11px", fontWeight: "700", padding: "2px 8px", borderRadius: "5px",
    background: "rgba(37,99,235,0.1)", color: "#2563EB", border: "1px solid rgba(37,99,235,0.2)", fontFamily: "monospace",
  },
  rowMeta: { fontSize: "12px", color: "var(--text-muted)", marginTop: "4px" },
  rowActions: { display: "flex", gap: "6px", flexShrink: 0 },
};

const F = {
  form: { display: "flex", flexDirection: "column", gap: "14px" },
  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" },
  footer: { display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px", borderTop: "1px solid var(--surface-border)", marginTop: "4px" },
};
